/**
 * Why an alert fired, in the terms of the rule that raised it — the alert record's answer to
 * "what exactly happened?". Reads `alert.details` (command-center migration 063,
 * ruleConditions.ruleEvidence):
 *
 *   Value            Duration             Confidence
 *   132              5m 2s                92%
 *   below 150        held ≥ 5m            minimum 50%
 *
 * One tile per rule condition, with what the event reported large and the rule's threshold
 * under it; a tile for how long the condition held when the rule has a hold time (or, failing
 * that, a duration the model itself reported — the belt model's idle time); and confidence
 * ONLY when the rule actually judged it. The remaining reported values follow as a list.
 *
 * Nothing here knows a model: panel OCR, belt movement and whatever comes next all render
 * from the rule's conditions and the event's metadata. Same component, local and cloud.
 */
import { formatSeconds } from './ruleConditionsModel';
import { toneVar } from '../common/StatusTag';
import { alertSeverityTone } from '../alerts/AlertTags';

interface EvidenceRule {
  field: string;
  op: string;
  value?: unknown;
  actual?: unknown;
}

export interface AlertDetails {
  conditions?: { match: 'all' | 'any'; rules: EvidenceRule[] } | null;
  sustain_seconds?: number;
  sustained_s?: number | null;
  confidence?: { applied?: boolean; min?: number; reported?: number | null };
  reported?: Record<string, unknown>;
}

function asDetails(raw: unknown): AlertDetails | null {
  let d: any = raw;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch { return null; }
  }
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
}

/**
 * Whether the alert's confidence means anything: only when the rule set a threshold and the
 * model reported a score to hold against it. Otherwise the stored number is a placeholder
 * (0 for "no score") or a score nothing looked at, and showing it misleads.
 */
export function confidenceConsidered(alert: any): boolean {
  return !!asDetails(alert?.details)?.confidence?.applied;
}

/**
 * The model's own running number for this alert, when it sends one (the belt model counts its
 * stops: `alert_num`). The record's header reads "Alert #3" from it. Null for models that don't.
 */
export function alertNumber(alert: any): number | null {
  const r = asDetails(alert?.details)?.reported;
  const n = r ? num((r as any).alert_num ?? (r as any).alertNum) : null;
  return n !== null && n >= 0 ? Math.round(n) : null;
}

const titleCase = (s: string) =>
  s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (m) => m.toUpperCase());

/** A field that holds seconds: idle_s, low_for_s, sustained_s, stopped_seconds, duration. */
const isDurationField = (field: string) => /(_s|_sec|_secs|_seconds)$|duration/i.test(field);

/** "low_for_s" → "Low For", "idle_s" → "Idle", "value" → "Value". */
function fieldLabel(field: string): string {
  const last = field.split('.').pop() || field;
  return titleCase(last.replace(/(_s|_sec|_secs|_seconds)$/i, '')) || field;
}

const num = (v: unknown) => (v === '' || v === null || v === undefined || typeof v === 'boolean' ? null : Number.isFinite(Number(v)) ? Number(v) : null);

function show(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = num(v);
  if (n !== null && isDurationField(field)) return formatSeconds(Math.round(n));
  if (n !== null) return String(Math.round(n * 1000) / 1000);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return /^[a-z0-9]+(_[a-z0-9]+)+$/.test(String(v)) ? titleCase(String(v)) : String(v);
}

/** The rule's side of a condition, in words: "below 150", "is No Belt Movement". */
function ruleText(r: EvidenceRule): string {
  const v = (x: unknown) => show(r.field, x);
  const pair = Array.isArray(r.value) ? r.value : [];
  switch (r.op) {
    case 'lt': return `below ${v(r.value)}`;
    case 'lte': return `at or below ${v(r.value)}`;
    case 'gt': return `above ${v(r.value)}`;
    case 'gte': return `at least ${v(r.value)}`;
    case 'eq': return `is ${v(r.value)}`;
    case 'neq': return `is not ${v(r.value)}`;
    case 'between': return `between ${v(pair[0])} – ${v(pair[1])}`;
    case 'outside': return `outside ${v(pair[0])} – ${v(pair[1])}`;
    case 'in': return `one of ${pair.map(v).join(', ')}`;
    case 'not_in': return `not one of ${pair.map(v).join(', ')}`;
    case 'contains': return `contains ${v(r.value)}`;
    case 'exists': return 'is reported';
    case 'not_exists': return 'is missing';
    default: return `${r.op} ${v(r.value)}`;
  }
}

/**
 * The severity's colour, darkened toward the text colour so a reading printed in it is still
 * legible on the card — the raw warning amber on a pale card is a highlighter, not text.
 */
const inkFor = (severity?: string | null) =>
  `color-mix(in srgb, ${toneVar(alertSeverityTone(severity))} 72%, var(--foreground))`;

/** The rule's conditions as one sentence: "State was No Belt Movement for 3s." */
function sentenceFor(rules: EvidenceRule[], match: 'all' | 'any' | undefined, held: string | null): string {
  const parts = rules.map((r) => {
    const label = fieldLabel(r.field);
    const actual = show(r.field, r.actual);
    switch (r.op) {
      case 'eq': return `${label} was ${actual}`;
      case 'exists': return `${label} was reported (${actual})`;
      case 'not_exists': return `${label} was missing`;
      default: return `${label} ${actual}, ${ruleText(r)}`;
    }
  });
  if (!parts.length) return '';
  const text = parts.join(match === 'any' ? ' or ' : ' and ') + (held ? ` for ${held}` : '');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

function Tile({ label, value, sub, title, ink }: { label: string; value: string; sub?: string; title?: string; ink: string }) {
  return (
    /* Every tile draws its left and top rules and the grid is pulled 1px up-left inside a
       clipping box, so the outermost rules vanish and a wrapped second row still gets its
       divider — no border hanging off the edge whichever way the tiles wrap. */
    <div className="min-w-[9rem] flex-1 border-l border-t border-border px-4 py-3.5" title={title}>
      <div className="truncate text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-[26px] font-bold leading-tight tabular-nums" style={{ color: ink }}>{value}</div>
      {sub && <div className="mt-1 truncate text-[13px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function AlertTriggerDetails({ alert, className = '' }: { alert: any; className?: string }) {
  const d = asDetails(alert?.details);
  if (!d) return null;

  const rules = d.conditions?.rules || [];
  const reported = d.reported && typeof d.reported === 'object' ? d.reported : {};
  const sustain = Number(d.sustain_seconds) || 0;
  const sustained = num(d.sustained_s);
  const shown = new Set(rules.map((r) => r.field));
  const ink = inkFor(alert?.severity);
  let held: string | null = null;

  const tiles: { key: string; label: string; value: string; sub?: string; title?: string }[] = rules.map((r, i) => ({
    key: `c${i}`,
    label: fieldLabel(r.field),
    value: show(r.field, r.actual),
    sub: `rule: ${ruleText(r)}`,
    title: `${r.field} — reported ${String(r.actual ?? '—')}`,
  }));

  // How long it lasted: the hold time the evaluator measured, else a duration the model
  // reported itself (the belt model sends one stop event that says how long it has been idle).
  if (sustain > 0 || sustained !== null) {
    held = sustained !== null ? formatSeconds(Math.round(sustained)) : null;
    tiles.push({
      key: 'held',
      label: 'Duration',
      value: held ?? '—',
      sub: sustain > 0 ? `rule: held at least ${formatSeconds(sustain)}` : undefined,
    });
  } else {
    const dur = Object.keys(reported).find((k) => isDurationField(k) && !shown.has(k) && num(reported[k]) !== null);
    if (dur) {
      held = show(dur, reported[dur]);
      tiles.push({ key: 'dur', label: fieldLabel(dur), value: held, sub: 'reported by the model', title: dur });
      shown.add(dur);
    }
  }

  if (d.confidence?.applied && num(d.confidence.reported) !== null) {
    const pct = (x: number) => `${Math.round(x <= 1 ? x * 100 : x)}%`;
    tiles.push({
      key: 'conf',
      label: 'Confidence',
      value: pct(Number(d.confidence.reported)),
      sub: d.confidence.min ? `rule: minimum ${pct(Number(d.confidence.min))}` : undefined,
    });
  }

  // Whatever else the model reported — the threshold it was reading against, the zone, … —
  // minus what the tiles already say, and minus the model's own confidence when the rule
  // never judged it.
  const rest = Object.entries(reported).filter(([k]) =>
    !shown.has(k) && (d.confidence?.applied || !/^(conf|confidence|confidence_score)$/i.test(k)));

  if (tiles.length === 0 && rest.length === 0) return null;

  const sentence = sentenceFor(rules, d.conditions?.match, held);

  return (
    <section
      className={`overflow-hidden rounded-xl border border-border ${className}`}
      // A tint of the muted surface rather than `bg-muted/40`: the theme colours are bare
      // var()s, and Tailwind drops the opacity modifier on those — the class compiles to nothing.
      style={{ backgroundColor: 'color-mix(in srgb, var(--muted) 45%, transparent)' }}
    >
      <header className="flex flex-wrap items-baseline gap-x-5 gap-y-1 px-4 py-3">
        <h3 className="text-[12px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Why this alert fired
          {rules.length > 1 && (
            <span className="ml-2 normal-case tracking-normal font-normal">
              ({d.conditions?.match === 'any' ? 'any condition' : 'all conditions'})
            </span>
          )}
        </h3>
        {sentence && <p className="text-[16px] font-semibold" style={{ color: ink }}>{sentence}</p>}
      </header>
      {tiles.length > 0 && (
        <div className="overflow-hidden">
          <div className="-ml-px flex flex-wrap">
            {tiles.map(({ key, ...t }) => <Tile key={key} {...t} ink={ink} />)}
          </div>
        </div>
      )}
      {rest.length > 0 && (
        <dl className="flex flex-wrap gap-x-8 gap-y-1 border-t border-border px-4 py-2.5 text-[14px]">
          {rest.map(([k, v]) => (
            <div key={k} className="flex gap-2" title={k}>
              <dt className="text-muted-foreground">{fieldLabel(k)}</dt>
              <dd className="font-semibold tabular-nums" style={{ color: ink }}>{show(k, v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
